import { applyTheme, createShadowRoot, element, renderSuccess } from './dom';
import { emitWidgetEvent, trackCaptureEvent } from './events';
import { createContactCaptureForm } from './form';
import type { PublicConfig, WidgetSource } from './types';

/** Keeps Tab inside the popup, and closes it on Escape. */
const trapFocus = (shadow: ShadowRoot, close: () => void) => {
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(shadow.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled])',
    ));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && shadow.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && shadow.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  document.addEventListener('keydown', onKeyDown);
  return () => document.removeEventListener('keydown', onKeyDown);
};

export const renderLoadError = (source: WidgetSource, error: unknown) => {
  if (source.mode === 'popup') {
    source.host.remove();
    return;
  }
  source.host.setAttribute('data-bhi-mode', source.mode);
  const shadow = createShadowRoot(source.host);
  const message = error instanceof Error ? error.message : 'The contact form could not be loaded.';
  shadow.appendChild(element('div', 'bhi-load-error', message));
};

export const renderContactCapture = (source: WidgetSource, config: PublicConfig) => {
  const { content } = config.settings;
  const isPopup = source.mode === 'popup';
  source.host.setAttribute('data-bhi-mode', source.mode);
  applyTheme(source.host, config);
  const shadow = createShadowRoot(source.host);
  const previouslyFocused = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;

  const frame = element('div', isPopup ? 'bhi-overlay' : 'bhi-inline-frame');
  if (isPopup) frame.setAttribute('role', 'presentation');

  const card = element('section', 'bhi-card');
  const showTitle = source.mode !== 'inline' || content.showTitle;
  if (showTitle) card.setAttribute('aria-labelledby', `bhi-title-${config.id}`);
  if (isPopup) {
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
  }

  let releaseFocusTrap: (() => void) | null = null;
  let focusTimer: number | undefined;
  let closed = false;
  const dispose = () => {
    closed = true;
    releaseFocusTrap?.();
    releaseFocusTrap = null;
    window.clearTimeout(focusTimer);
    source.host.remove();
  };
  const close = () => {
    if (closed) return;
    if (isPopup) {
      void trackCaptureEvent(source, 'dismissed');
      dispose();
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    }
    emitWidgetEvent('bhi:capture:closed', config.id);
  };

  if (isPopup) {
    const closeButton = element('button', 'bhi-close', '×');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Close contact form');
    closeButton.addEventListener('click', close);
    card.appendChild(closeButton);
  }

  if (showTitle) {
    const header = element('header', 'bhi-header');
    const { brandNameAlign, headerTextAlign } = config.settings.styles;
    const brand = element('p', 'bhi-brand', content.brandName);
    const title = element('h2', 'bhi-title', content.headerText);
    title.id = `bhi-title-${config.id}`;
    // Centred or right-aligned text would run under the close button, so both sides clear it.
    // Inset per line, so aligning one line does not shift the other.
    if (isPopup && brandNameAlign !== 'left') brand.classList.add('bhi-header-inset');
    if (isPopup && headerTextAlign !== 'left') title.classList.add('bhi-header-inset');
    header.appendChild(brand);
    header.appendChild(title);
    card.appendChild(header);
  }

  const body = element('div', 'bhi-body');
  if (content.bodyText) body.appendChild(element('p', 'bhi-description', content.bodyText));
  body.appendChild(createContactCaptureForm({ body, config, onDecline: close, source }));
  if (source.previewState === 'success') renderSuccess(body, content.successMessage);
  card.appendChild(body);

  if (config.consentText) card.appendChild(element('p', 'bhi-consent', config.consentText));
  frame.appendChild(card);
  shadow.appendChild(frame);

  if (isPopup) {
    releaseFocusTrap = trapFocus(shadow, close);
    // The builder preview re-renders on every keystroke; autofocus would pull focus out of the editor.
    if (!source.host.hasAttribute('data-bhi-preview')) {
      focusTimer = window.setTimeout(() => shadow.querySelector<HTMLInputElement>('.bhi-input')?.focus(), 0);
    }
  }

  void trackCaptureEvent(source, 'viewed');
  if (isPopup) void trackCaptureEvent(source, 'opened');
  emitWidgetEvent('bhi:capture:loaded', config.id, { mode: source.mode });
  emitWidgetEvent('bhi:capture:opened', config.id, { mode: source.mode });
  return dispose;
};
